import { B2CCartProcessTile } from "../B2CCartProcessTile/B2CCartProcessTile";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import classNames from "classnames";
import classes from "./B2CRequestPage.module.less";
import { useShowAffiliateMainBanner } from "~/feature/Affiliate/utils";
import InformationContainer from "./components/InformationContainer";
import { useDispatch, useSelector } from "react-redux";
import { useOnMount } from "~/common/hooks/useOnMount";
import { cartPageLoadThunk } from "../../../redux/cartPageLoadThunk";
import { type RootState } from "~/redux/store";
import { type LineItem } from "~/Cart/models/CartData";
import { type ProductData } from "~/models/ProductData";
import B2CRequestCartList from "./components/B2CRequestCart/B2CRequestCartList";
import { Button } from "~/common/components/Button/Button";
import { useRef, useState } from "react";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { requestCart } from "./api/requestCart";
import { type SalesPartnerInfo, type UserInfoData } from "./types";
import { t } from "i18next";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { transformSalesPartnerData } from "./utils";
import { format } from "~/api/ApiHelper";
import { AnalyticsMQ } from "~/feature/Analytics/AnalyticsMQ/AnalyticsMQ";
import { selectProductDescriptor } from "~/redux/products/productsSelectors";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";
import { FormProvider, useForm } from "react-hook-form";
import { InquirySuccessPage } from "~/InquirySuccess/components/InquirySuccessPage";
import { userIsLoggedIn } from "~/utils/roles";

const REQUIRED_INFO_FIELDS: (keyof UserInfoData)[] = ["company", "salutationCode", "firstName", "lastName", "country"];

interface B2CRequestPageProps {
  userInfo: UserInfoData;
  salesPartner: SalesPartnerInfo;
  marketingConsent: boolean;
}

export interface DataObject {
  requestNumber: string;
  userDetails: UserInfoData;
  salesPartnerData: SalesPartnerInfo;
}

export interface CartElement {
  data: LineItem;
  product: ProductData;
  descriptor: ProductDescriptor;
}

interface CheckboxesForm {
  userAgreement: boolean;
  tcAgreement: boolean;
  ppAgreement: boolean;
}

//copied from B2CCart
const selectElements = (root: RootState) => {
  const cart = root.cart;
  const products = root.products;
  const lineItems = cart.cartEntries
    .map((id) => cart.cartEntriesById[id])
    .flatMap((entry) => entry.packEntryIds.map((id) => cart.packEntriesById[id]));

  const cartElements: CartElement[] = lineItems.map((lineItem) => ({
    data: lineItem,
    product: root.products.basic[lineItem.productCode],
    descriptor: selectProductDescriptor(root, lineItem),
  }));

  const packEntries = cart.packEntriesById;
  return { cartElements, packEntries, products };
};

const B2CRequestPage = ({ userInfo, salesPartner, marketingConsent }: B2CRequestPageProps) => {
  const initialValues: DataObject = (() => {
    const userBase: UserInfoData = {
      company: userInfo.company,
      titleCode: userInfo.titleCode,
      salutationCode: userInfo.salutationCode,
      firstName: userInfo.firstName,
      lastName: userInfo.lastName,
      line1: userInfo.line1, // Line 1 contains house number and street
      additionalLine1: userInfo.additionalLine1, // Additional address line (displayed after Line1)
      line2: userInfo.line2, // Line2 contains the city, region and postalcode
      zipCode: userInfo.zipCode,
      city: userInfo.city,
      country: userInfo.country,
      phone: userInfo.phone,
      email: userInfo.email,
      updateUser: true,
    };

    return {
      requestNumber: "",
      userDetails: userBase,
      salesPartnerData: salesPartner,
    };
  })();

  const infoRef = useRef<HTMLDivElement>(null);

  const isDesktop = useIsMinDesktopSize();
  const isTablet = useIsMinTabletSize();

  const isAffiliate = useShowAffiliateMainBanner();
  const formProps = useForm<CheckboxesForm>({
    defaultValues: {
      userAgreement: marketingConsent,
      tcAgreement: false,
      ppAgreement: false,
    },
  });

  const { val: showSuccessScreen, setTrue: displaySuccessScreen } = useBooleanState(false);

  const dispatch = useDispatch();

  const [dataObject, setDataObject] = useState<DataObject>(initialValues);

  useOnMount(() => {
    dispatch(cartPageLoadThunk());
  });

  const hasAllUserInfo = REQUIRED_INFO_FIELDS.every((prop) => {
    if (dataObject.userDetails[prop] === "") {
      return false;
    }
    return Object.prototype.hasOwnProperty.call(dataObject.userDetails, prop);
  });

  const items = useSelector(selectElements);

  const onButtonClick = formProps.handleSubmit((data) => {
    const { userAgreement, tcAgreement, ppAgreement } = data;
    requestCart({
      requestNumber: dataObject.requestNumber,
      tcConsent: tcAgreement,
      ppConsent: ppAgreement,
      marketingConsent: userAgreement,
    }).then(() => {
      const id = format(new Date(), "YYYYDDDDHHmmssSSS");
      window.SCHA.updateMinicart();

      AnalyticsMQ.instance.push("placeOrder", {
        orderId: id,
        type: "quote",
        paymentType: "Sales Partner Order",
        entries: items.cartElements.map((item) => ({
          lineItem: item.data,
          product: item.descriptor,
        })),
        salesPartner: transformSalesPartnerData(dataObject.salesPartnerData),
      });

      displaySuccessScreen();
    });
  });

  let inquirySuccessUserData = undefined;
  if (!userIsLoggedIn()) {
    inquirySuccessUserData = {
      registered: userInfo.registered ?? false,
      name: userInfo.firstName + " " + userInfo.lastName,
      company: userInfo.company,
      email: userInfo.email,
      address: userInfo.line1 + " " + userInfo.line2,
      marketingConsent: formProps.watch("userAgreement"),
    };
  }

  return (
    <div className={classNames(classes.root)}>
      {!isAffiliate && !showSuccessScreen && <B2CCartProcessTile activeIndex={2} />}
      <div className={classNames(!showSuccessScreen && classes.topMargin)}>
        {(!showSuccessScreen || isTablet) && <PageTitle>{t("page.cart.b2c.process.step.3")}</PageTitle>}
      </div>

      {!showSuccessScreen && (
        <>
          <FormProvider {...formProps}>
            <InformationContainer
              ref={infoRef}
              onButtonClick={() => {
                onButtonClick();
              }}
              marketingConsent={marketingConsent}
              dataObject={dataObject}
              setDataObject={setDataObject}
              className="mb-30"
              hasAllUserInfo={hasAllUserInfo}
            />
          </FormProvider>

          <p className="headline-medium">{t("cartPage.toBeRequested", { count: items.cartElements.length })}</p>
          <B2CRequestCartList items={items.cartElements} />
          <div className={classes.footer}>
            <Button
              onClick={() => {
                onButtonClick();
              }}
              fullWidth
              disabled={!hasAllUserInfo}
            >
              {t("page.cart.b2c.process.step.3")}
            </Button>
          </div>
        </>
      )}
      {showSuccessScreen && <InquirySuccessPage userData={inquirySuccessUserData} />}
    </div>
  );
};

export default B2CRequestPage;

export function createB2CRequestPage(data: B2CRequestPageProps) {
  return <B2CRequestPage {...data} />;
}
